// M143 — /berichten — Post-generator galerij + generator.
// Vier posts per week (LinkedIn, Facebook, Instagram, TikTok). Klant
// kopieert 1-klik en plaatst zelf. JustEasySEO blijft in Adviesmodus.
import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { useEmbed } from "@/contexts/EmbedContext";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Plus, Copy, Check, Trash2, X, Sparkles, MessageSquare,
  Linkedin, Facebook, Instagram, Music,
} from "lucide-react";

const PLATFORMS = [
  { key: "linkedin", label: "LinkedIn", icon: Linkedin, color: "text-info-blue" },
  { key: "facebook", label: "Facebook", icon: Facebook, color: "text-accent-teal" },
  { key: "instagram", label: "Instagram", icon: Instagram, color: "text-warn-amber" },
  { key: "tiktok", label: "TikTok", icon: Music, color: "text-neg-red" },
];

function ConfirmModal({ title, body, confirmLabel, cancelLabel, tone, onConfirm, onCancel }) {
  return (
    <div data-testid="berichten-confirm-modal"
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full rounded-card border border-border-hairline bg-bg-panel p-5">
        <div className="text-sm font-display text-text-primary mb-2">{title}</div>
        <p className="text-xs text-text-primary leading-relaxed mb-4">{body}</p>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} data-testid="berichten-confirm-cancel"
                   className="text-[11px] px-3 py-1.5 rounded border border-border-hairline text-text-muted">
            {cancelLabel || "Annuleren"}
          </button>
          <button onClick={onConfirm} data-testid="berichten-confirm-ok"
                   className={`text-[11px] px-3 py-1.5 rounded border ${ tone === "danger" ? "border-neg-red/40 text-neg-red hover:bg-neg-red/10" : "border-accent-teal/40 text-accent-teal hover:bg-accent-teal/10"}`}>
            {confirmLabel || "Bevestigen"}
          </button>
        </div>
      </div>
    </div>
  );
}

function GenerateModal({ themes, pages, busy, onSubmit, onClose }) {
  const [page, setPage] = useState("");
  const [theme, setTheme] = useState("");
  return (
    <div data-testid="berichten-generate-modal"
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full rounded-card border border-border-hairline bg-bg-panel p-5">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-accent-teal" />
          <div className="text-sm font-display text-text-primary">Nieuwe berichten laten maken</div>
        </div>
        <p className="text-xs text-text-muted mb-4">
          JustEasySEO maakt vier posts (LinkedIn, Facebook, Instagram, TikTok).
          Kies optioneel een pagina om op te focussen of een thema.
        </p>
        <div className="space-y-3">
          <div>
            <div className="text-[10px] text-text-muted mb-1">
              Focus op deze pagina (optioneel)
            </div>
            <select value={page} onChange={e => setPage(e.target.value)}
                     data-testid="berichten-modal-page"
                     className="w-full text-sm bg-bg-panel-raised border border-border-hairline rounded px-2 py-1.5 text-text-primary">
              <option value="">Geen — vrij bericht</option>
              {pages.map(p => (
                <option key={p.page_id} value={p.page_id}>{p.title || p.url}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="text-[10px] text-text-muted mb-1">
              Thema (optioneel)
            </div>
            <select value={theme} onChange={e => setTheme(e.target.value)}
                     data-testid="berichten-modal-theme"
                     className="w-full text-sm bg-bg-panel-raised border border-border-hairline rounded px-2 py-1.5 text-text-primary">
              <option value="">Vrij — laat JustEasySEO kiezen</option>
              {themes.map(t => (<option key={t} value={t}>{t}</option>))}
            </select>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} data-testid="berichten-modal-close"
                   className="text-[11px] px-3 py-1.5 rounded border border-border-hairline text-text-muted">
            Annuleren
          </button>
          <button onClick={() => onSubmit({ page_id: page || null, theme: theme || null })}
                   disabled={busy} data-testid="berichten-modal-submit"
                   className="text-[11px] px-3 py-1.5 rounded border border-accent-teal/40 text-accent-teal hover:bg-accent-teal/10 flex items-center gap-1 disabled:opacity-50">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            Maak vier berichten
          </button>
        </div>
      </div>
    </div>
  );
}

function PostCard({ post, index, onCopied, onDelete }) {
  const P = PLATFORMS.find(p => p.key === post.platform) || PLATFORMS[0];
  const Icon = P.icon;
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    const text = [post.hook_nl, post.caption_nl,
                  (post.hashtags || []).join(" ")]
      .filter(Boolean).join("\n\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Bericht gekopieerd. Plak 'm in " + P.label + ".");
      onCopied?.(post.post_id);
      setTimeout(() => setCopied(false), 3500);
    } catch (_e) {
      toast.error("Kopiëren mislukt — selecteer de tekst handmatig.");
    }
  };
  return (
    <div data-testid={`berichten-card-${post.platform}-${index}`}
          className="rounded-card border border-border-hairline bg-bg-panel p-4 flex flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        <Icon className={`w-4 h-4 ${P.color}`} />
        <div className="text-sm font-bold text-text-primary">{P.label}</div>
        <span className="ml-auto text-xs text-text-muted">
          {post.status === "gekopieerd_door_klant" ? "Gekopieerd" : "Klaar"}
        </span>
      </div>
      {post.hook_nl && (
        <div className="text-sm font-semibold text-text-primary leading-snug"
              data-testid={`berichten-hook-${post.platform}-${index}`}>
          {post.hook_nl}
        </div>
      )}
      <div
        className="text-sm text-text-primary whitespace-pre-wrap font-sans leading-relaxed rounded-[12px] p-3"
        style={{ background: "#F7F8F5" }}
        data-testid={`berichten-caption-${post.platform}-${index}`}
      >
        {post.caption_nl}
      </div>
      {(post.hashtags || []).length > 0 && (
        <div className="text-[11px] text-info-blue leading-snug break-words"
              data-testid={`berichten-hashtags-${post.platform}-${index}`}>
          {(post.hashtags || []).join(" ")}
        </div>
      )}
      {post.beeld_idee_nl && (
        <div className="rounded border border-border-hairline bg-bg-panel-raised p-2 mt-1">
          <div className="text-[10px] text-text-muted mb-1">
            Beeld-idee
          </div>
          <div className="text-[11px] text-text-primary italic">{post.beeld_idee_nl}</div>
        </div>
      )}
      {post.linked_page_url && (
        <div className="text-[10px] text-text-subtle italic truncate">
          Voor pagina: <a href={post.linked_page_url} target="_blank" rel="noopener noreferrer"
                            className="text-accent-teal hover:underline">{post.linked_page_url}</a>
        </div>
      )}
      <div className="flex gap-2 mt-2">
        <button onClick={copy} data-testid={`berichten-copy-${post.platform}-${index}`}
                 className="flex-1 text-[11px] px-3 py-1.5 rounded border border-accent-teal/40 text-accent-teal hover:bg-accent-teal/10 flex items-center justify-center gap-1">
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? "Gekopieerd" : "Kopieer bericht"}
        </button>
        <button onClick={() => onDelete(post.post_id)}
                 data-testid={`berichten-delete-${post.platform}-${index}`}
                 className="text-[11px] px-2 py-1.5 rounded border border-border-hairline text-text-muted hover:text-neg-red hover:border-neg-red/40">
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

export default function BerichtenPage() {
  const { hideInnerHeader } = useEmbed();
  const [data, setData] = useState(null);
  const [pages, setPages] = useState([]);
  const [showGen, setShowGen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      const [gal, top] = await Promise.all([
        api.get("/posts/gallery"),
        api.get("/pages/top10"),
      ]);
      setData(gal.data);
      setPages(top.data.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail?.message_nl
        || "Kon berichten niet laden. Probeer het opnieuw.");
    }
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const items = data?.items || [];
    const byWeek = new Map();
    for (const it of items) {
      const key = it.week_iso || "onbekend";
      if (!byWeek.has(key)) byWeek.set(key, []);
      byWeek.get(key).push(it);
    }
    return Array.from(byWeek.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [data]);

  const generate = async ({ page_id, theme }) => {
    setBusy(true); setErr(null);
    try {
      await api.post("/posts/generate", { page_id, theme });
      toast.success("Vier nieuwe berichten gemaakt.");
      setShowGen(false);
      await load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      const msg = det?.message_nl || "Berichten maken lukte niet.";
      setErr(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const markCopied = async (postId) => {
    try { await api.post(`/posts/${postId}/mark-copied`); await load(); }
    catch (_e) {}
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/posts/${confirmDel}`);
      setConfirmDel(null);
      toast.success("Bericht verwijderd.");
      await load();
    } catch (_e) {
      toast.error("Verwijderen mislukt.");
    }
  };

  const items = data?.items || [];

  return (
    <AppLayout>
      {!hideInnerHeader && (
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-text-primary tracking-tight flex items-center gap-2"
                 data-testid="berichten-title">
              <MessageSquare className="w-5 h-5 text-accent-teal" /> Berichten
            </h1>
            <p className="text-sm text-text-muted mt-1">
              LinkedIn, Facebook, Instagram en TikTok — kopieer en plaats zelf.
            </p>
          </div>
        </div>
      )}
      <div className="mb-6 flex justify-end">
        <button onClick={() => setShowGen(true)} disabled={busy}
                 data-testid="berichten-new-btn"
                 className="inline-flex items-center gap-1.5 rounded-[10px] px-4 py-2 bg-accent-teal text-white text-sm font-medium hover:bg-accent-teal-dim disabled:opacity-50"
                 style={{ boxShadow: "0 1px 3px rgba(8,127,140,0.24)" }}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Maak 4 nieuwe berichten
        </button>
      </div>

      <div data-testid="berichten-banner"
            className="rounded-card border border-accent-teal/30 bg-accent-teal/5 px-4 py-3 mb-5">
        <div className="text-sm text-accent-teal font-semibold">
          JustEasySEO maakt de post, jij kopieert 'm en plaatst zelf.
        </div>
        <div className="text-[11px] text-text-muted mt-0.5">
          Zo houd je zelf de regie over je socials.
        </div>
      </div>

      {err && (
        <div data-testid="berichten-error"
              className="mb-4 rounded border border-warn-amber/40 bg-warn-amber/10 px-3 py-2 text-xs text-warn-amber flex items-start justify-between gap-2">
          <span>{err}</span>
          <button onClick={() => setErr(null)}><X className="w-3 h-3" /></button>
        </div>
      )}

      {!data ? (
        <div className="text-text-muted text-sm flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Berichten laden…
        </div>
      ) : items.length === 0 ? (
        <div data-testid="berichten-empty"
              className="rounded-card border border-dashed border-border-hairline bg-bg-panel p-8 text-center">
          <MessageSquare className="w-8 h-8 text-text-muted mx-auto mb-3" />
          <div className="text-sm font-display text-text-primary mb-2">Nog geen berichten</div>
          <p className="text-xs text-text-muted max-w-md mx-auto mb-4 leading-relaxed">
            Vier goede posts per week houden je zichtbaar zonder dat je zelf tekst hoeft te
            bedenken. Laat JustEasySEO de eerste ronde maken.
          </p>
          <button onClick={() => setShowGen(true)} disabled={busy}
                   data-testid="berichten-empty-cta"
                   className="text-[11px] px-3 py-1.5 rounded border border-accent-teal/40 text-accent-teal hover:bg-accent-teal/10">
            Maak mijn eerste 4 berichten
          </button>
        </div>
      ) : (
        <div className="space-y-8" data-testid="berichten-weeks">
          {grouped.map(([week, posts]) => (
            <div key={week}>
              <div className="text-[11px] text-text-muted mb-3">
                Week {week}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {PLATFORMS.map((P) => {
                  const p = posts.find(x => x.platform === P.key);
                  if (!p) return null;
                  return (
                    <PostCard key={p.post_id} post={p} index={0}
                              onCopied={markCopied}
                              onDelete={(id) => setConfirmDel(id)} />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {showGen && (
        <GenerateModal
          themes={data?.themes || []}
          pages={pages}
          busy={busy}
          onClose={() => setShowGen(false)}
          onSubmit={generate}
        />
      )}
      {confirmDel && (
        <ConfirmModal
          title="Bericht verwijderen?"
          body="Deze post wordt uit jouw galerij verwijderd. Je kunt 'm daarna opnieuw laten maken."
          confirmLabel="Verwijderen" cancelLabel="Annuleren" tone="danger"
          onConfirm={doDelete} onCancel={() => setConfirmDel(null)}
        />
      )}
    </AppLayout>
  );
}
